import { Column, getStringifiedParams, isFilter, QueryParamName } from "shared";

import { getFilteredValues } from "../params/functions";
import {
    deleteUpdatedQueryParams,
    getActiveQueryParams,
    getSelectableRows,
    getTotalRows,
    getUpdatedRowsAfterMultipleRowsDelete,
    getUpdatedRowsAfterSingleRowUpdate,
    getUpdatedSelectedRows,
    setRows,
    updateSelectedRows
} from "./functions";
import {
    ADD_BLINKED_ROW,
    DELETE_MULTIPLE_ROWS,
    DESELECT_ALL_AND_SELECT_MULTIPLE,
    DESELECT_ALL_ROWS,
    DESELECT_MULTIPLE_ROWS,
    REMOVE_BLINKED_ROW,
    RESET_INNER_TABLE_ALL_PARAMS,
    RESET_INNER_TABLE_FILTER_PARAMS,
    RESTORE_ACTIVE_COLUMNS,
    SELECT_MULTIPLE_ROWS,
    SET_CUSTOM_RESOURCE,
    SET_DETAILS_DATA,
    SET_INNER_TABLE_PARAMS,
    SET_TABLE_DATA,
    SET_TABLE_LOADING,
    TOGGLE_ACTIVE_COLUMN,
    TOGGLE_SELECTION_OF_SINGLE_ROW,
    UPDATE_SINGLE_ROW_DATA
} from "./tableActions";
import { Action, TableInitialState } from "./types";

const TableReducer = (
    state: TableInitialState,
    action: Action
): TableInitialState => {
    switch (action.type) {
        case SET_TABLE_DATA: {
            const {
                tableName,
                isNotSelectable,
                hasLocalFiltering,
                innerTableQueryParams
            } = state;

            const { outerDetailsData, rows, user, t, isLoading, ...rest } =
                action.payload;

            return {
                ...state,
                ...rest,
                rows: setRows(
                    hasLocalFiltering,
                    tableName,
                    innerTableQueryParams,
                    rows,
                    rows,
                    t
                ),
                innerTableInitialRows: rows,
                selectableRows: getSelectableRows(
                    tableName,
                    isNotSelectable,
                    rows,
                    user
                ),
                isInitialLoading: isLoading ?? false,
                isTableLoading: isLoading ?? false,
                outerDetailsData: outerDetailsData || null
            };
        }
        case SET_INNER_TABLE_PARAMS: {
            const {
                tableName,
                rows,
                innerTableInitialRows,
                hasLocalFiltering
            } = state;

            const {
                updatedParams: queryParams,
                filteredValues,
                t
            } = action.payload;

            const isEmpty = Object.keys(queryParams).length === 0;

            const activeQueryParams = getActiveQueryParams(
                isEmpty,
                state,
                queryParams
            );

            const stringifiedParams = getStringifiedParams(activeQueryParams);

            const updatedFilteredValues = getFilteredValues(
                [...state.innerTableFilteredValues],
                filteredValues
            );

            return {
                ...state,
                rows: setRows(
                    hasLocalFiltering,
                    tableName,
                    activeQueryParams,
                    innerTableInitialRows,
                    rows,
                    t
                ),
                innerTableQueryParams: activeQueryParams,
                innerTableStringParams: stringifiedParams,
                innerTableFilteredValues: updatedFilteredValues
            };
        }
        case SET_CUSTOM_RESOURCE: {
            return {
                ...state,
                customResource: action.payload
            };
        }
        case RESET_INNER_TABLE_ALL_PARAMS: {
            const {
                tableName,
                hasLocalFiltering,
                innerTableQueryParams,
                innerTableInitialParamNames,
                rows,
                innerTableInitialRows
            } = state;

            const updatedQueryParams = innerTableQueryParams;
            const initialParams = innerTableInitialParamNames;

            const paramNames = Object.keys(
                updatedQueryParams
            ) as QueryParamName[];

            paramNames.forEach(key => {
                deleteUpdatedQueryParams(
                    initialParams,
                    key,
                    isFilter,
                    updatedQueryParams
                );
            });

            const stringifiedParams = getStringifiedParams(updatedQueryParams);

            return {
                ...state,
                rows: setRows(
                    hasLocalFiltering,
                    tableName,
                    updatedQueryParams,
                    innerTableInitialRows,
                    rows,
                    action.payload
                ),
                innerTableQueryParams: updatedQueryParams,
                innerTableStringParams: stringifiedParams,
                innerTableFilteredValues: []
            };
        }
        case RESET_INNER_TABLE_FILTER_PARAMS: {
            const {
                tableName,
                hasLocalFiltering,
                innerTableQueryParams,
                rows,
                innerTableInitialRows
            } = state;

            const updatedQueryParams = {
                ...innerTableQueryParams
            };

            const initialParams = state.innerTableInitialParamNames;

            const paramNames = Object.keys(
                updatedQueryParams
            ) as QueryParamName[];

            paramNames.forEach(key => {
                if (!initialParams.includes(key) && isFilter(key)) {
                    delete updatedQueryParams[key];
                }
            });

            const stringifiedParams = getStringifiedParams(updatedQueryParams);

            return {
                ...state,
                rows: setRows(
                    hasLocalFiltering,
                    tableName,
                    updatedQueryParams,
                    innerTableInitialRows,
                    rows,
                    action.payload
                ),
                innerTableQueryParams: updatedQueryParams,
                innerTableStringParams: stringifiedParams,
                innerTableFilteredValues: []
            };
        }
        case TOGGLE_ACTIVE_COLUMN: {
            const { tableName, columns } = state;

            const currentColumns = [...columns];
            const columnName = action.payload;

            const updatedColumns = currentColumns.map((column: Column) =>
                column.name === columnName
                    ? { ...column, isActive: !column.isActive }
                    : column
            );

            const setToLocalStorage = () => {
                type StorageColumns = Record<string, boolean>;
                const storageColumns: StorageColumns = {};

                updatedColumns.forEach(column => {
                    storageColumns[column.name] = column.isActive;
                });

                localStorage.setItem(
                    `${tableName}Columns`,
                    JSON.stringify(storageColumns)
                );
            };

            setToLocalStorage();

            return {
                ...state,
                columns: updatedColumns
            };
        }
        case RESTORE_ACTIVE_COLUMNS: {
            const { tableName, columns } = state;

            const currentColumns = [...columns];

            const updatedColumns = currentColumns.map(column => ({
                ...column,
                isActive: Boolean(column.isDefaultActive)
            }));

            const storageValue = Object.fromEntries(
                updatedColumns.map(col => [col.name, col.isActive])
            );

            localStorage.setItem(
                `${tableName}Columns`,
                JSON.stringify(storageValue)
            );

            return {
                ...state,
                columns: updatedColumns
            };
        }
        case SET_TABLE_LOADING: {
            return {
                ...state,
                isTableLoading: action.payload
            };
        }
        case TOGGLE_SELECTION_OF_SINGLE_ROW: {
            const rowId = action.payload;

            const updatedSelectedRows = updateSelectedRows(state, rowId);

            return {
                ...state,
                selectedRows: updatedSelectedRows
            };
        }
        case SELECT_MULTIPLE_ROWS: {
            return {
                ...state,
                selectedRows: [...state.selectedRows, ...action.payload]
            };
        }
        case DESELECT_ALL_AND_SELECT_MULTIPLE: {
            return {
                ...state,
                selectedRows: action.payload
            };
        }
        case DESELECT_MULTIPLE_ROWS: {
            const updatedRows = state.selectedRows.filter(
                row => !action.payload.includes(row)
            );

            return {
                ...state,
                selectedRows: updatedRows
            };
        }
        case DESELECT_ALL_ROWS: {
            return {
                ...state,
                selectedRows: []
            };
        }
        case SET_DETAILS_DATA: {
            const detailsData = action.payload;

            return {
                ...state,
                detailsData
            };
        }
        case UPDATE_SINGLE_ROW_DATA: {
            const {
                tableName,
                idType,
                isNotSelectable,
                rows,
                selectedRows,
                detailsData
            } = state;

            const { data: newData, user } = action.payload;
            const rowId = newData[idType];

            const updatedRows = getUpdatedRowsAfterSingleRowUpdate(
                rows,
                idType,
                rowId,
                newData
            );

            const updatedSelectableRows = getSelectableRows(
                tableName,
                isNotSelectable,
                updatedRows,
                user
            );

            const updatedSelectedRows = getUpdatedSelectedRows(
                selectedRows,
                updatedSelectableRows,
                idType
            );

            return {
                ...state,
                rows: updatedRows,
                innerTableInitialRows: updatedRows,
                selectableRows: updatedSelectableRows,
                selectedRows: updatedSelectedRows,
                detailsData:
                    detailsData && rowId === detailsData[idType]
                        ? newData
                        : detailsData
            };
        }
        case DELETE_MULTIPLE_ROWS: {
            const { idType, isNotSelectable, tableName, rows } = state;
            const { id: idArray, user } = action.payload;

            const updatedRows = getUpdatedRowsAfterMultipleRowsDelete(
                rows,
                idArray,
                idType
            );

            return {
                ...state,
                rows: updatedRows,
                innerTableInitialRows: updatedRows,
                selectableRows: getSelectableRows(
                    tableName,
                    isNotSelectable,
                    updatedRows,
                    user
                ),
                totalRows: getTotalRows(state.totalRows, idArray.length)
            };
        }
        case ADD_BLINKED_ROW: {
            const currentBlinkedRows = state.blinkedRows;
            const id = action.payload;

            return {
                ...state,
                blinkedRows: currentBlinkedRows.includes(id)
                    ? currentBlinkedRows
                    : [...currentBlinkedRows, id]
            };
        }
        case REMOVE_BLINKED_ROW: {
            return {
                ...state,
                blinkedRows: state.blinkedRows.filter(
                    rowId => rowId !== action.payload
                )
            };
        }
        default:
            return state;
    }
};

export default TableReducer;
